/**
 * =============================================================================
 * FORMATTING UTILITIES
 * =============================================================================
 *
 * Collection of utility functions for formatting values for display.
 * Shared by components and views so rendered output stays consistent.
 *
 * =============================================================================
 */

/**
 * Formats an ISO date string for display using the en-US locale
 *
 * Defaults to a short date such as "Jan 5, 2025". Any Intl.DateTimeFormat
 * options passed in are spread after the defaults, so they override or extend them.
 *
 * @param dateString - ISO date string to format
 * @param options - Optional Intl.DateTimeFormat options merged over the defaults
 * @returns Formatted date, or "N/A" if the input is empty
 *
 * @example
 * ```typescript
 * formatDate('2025-01-05T10:30:00Z') // → "Jan 5, 2025"
 * formatDate('2025-01-05T10:30:00Z', { month: 'long' }) // → "January 5, 2025"
 * formatDate(undefined) // → "N/A"
 * ```
 */
export function formatDate(
  dateString: string | null | undefined,
  options: Intl.DateTimeFormatOptions = {}
): string {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  })
}
